import { DateObjectUnits, DateTime, DateTimeJSOptions, Info } from "luxon";

import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { CHService } from "@app/project/model/ch-service";
import { FormatPhonePipe } from "../resources/phone-formatter.pipe";

@Injectable({
    providedIn: "root",
})
export class AppHelperService {
    // helper functions
    static defaultStr(strObj: any, column = "") {
        if (column) {
            return strObj[column] || "";
        }
        return strObj || "";
    }

    static numberSorting(s1: any, s2: any, column = "", sortDirection: "asc" | "desc" = "asc"): number {
        const val1 = +AppHelperService.defaultStr(s1, column);
        const val2 = +AppHelperService.defaultStr(s2, column);
        if (sortDirection === "asc") {
            return val1 >= val2 ? 1 : -1;
        }
        return val1 <= val2 ? 1 : -1;
    }

    static stringSorting(s1: any, s2: any, column = "", sortDirection: "asc" | "desc" = "asc"): number {
        const str1 = AppHelperService.defaultStr(s1, column);
        const str2 = AppHelperService.defaultStr(s2, column);
        if (sortDirection === "asc") {
            return str1.toLowerCase() >= str2.toLowerCase() ? 1 : -1;
        }
        return str1.toLowerCase() <= str2.toLowerCase() ? 1 : -1;
    }

    static getCurrentDateUTC() {
        return DateTime.now()
            .setLocale("en-us")
            .toUTC()
            .toISO()
            .toString()
            .replace(/\.\d{0,3}/, "");
    }

    static getLocalDate(date: string, hasTime = false) {
        const format = hasTime ? DateTime.DATETIME_SHORT_WITH_SECONDS : DateTime.DATE_FULL;
        return DateTime.fromISO(date, { zone: "utc" }).toLocal().toLocaleString(format);
    }

    static getCurrentDate() {
        return DateTime.now().toISO();
    }

    static getRelativeDateFromMilis(miliseconds = 0, timezone?: string) {
        return DateTime.fromMillis(miliseconds, { zone: timezone });
    }

    /**
     * handle onBlur event for form field
     *
     * @param theForm tracking form
     * @param field tracking field
     * @param format the output value format
     */
    static blurPhoneFieldHandler(theForm: FormGroup, field: string, format: string) {
        const theField = theForm.controls[field];
        const value: string = theField.value || "";
        const phoneParts = (value || "").match(/([+()-.\s0-9]+)/) || [null, value || ""];

        let formattedVal = "";

        if (phoneParts) {
            const digits: string = phoneParts[1].replace(/[()-. ]/g, "") || "";

            if (digits.length >= 10) {
                formattedVal = new FormatPhonePipe().transform(digits, format);
            } else {
                formattedVal = digits;
            }
        }

        theField.setValue(formattedVal, { emitEvent: false });
    }

    /**
     *
     * @param {CHService} service Service to check properties on
     * @returns {boolean} True if it is an intrusion peripheral devcie with playSirenType or siren control point
     */
    static isExternalSiren(service: CHService) {
        return (
            service?.definitionId === "com.clarecontrols.widgets.intrusion.peripheral" &&
            (!!service?.controlPoints.playSirenType || !!service?.controlPoints?.siren)
        );
    }

    /**
     *
     * @param {CHService} service Service to check properties on
     * @returns {boolean} True if it is a sonos intrusion peripheral device with playSirenType control point
     */
    static isExternalSonosSiren(service: CHService) {
        return service?.definitionId === "com.clarecontrols.widgets.intrusion.peripheral" && !!service?.controlPoints?.playSirenType;
    }

    /**
     * @description Puts the last element of the array as the first index to display weekdays as S M T W T F S
     * @returns an array of days of the week starting with sunday, ending with saturday
     */
    static setWeekdays(): string[] {
        const days = Info.weekdays("long").map(x => x.slice(0, 2));
        days.unshift(days.pop());
        return days;
    }

    static isUSZipcode(rawZipcode: string): boolean {
        const pattern = "^([0-9]){5}-[0-9]{4}";
        return new RegExp(pattern, "g").test(rawZipcode);
    }

    static convertLocalDateMilis(localMilis: number, opts: DateTimeJSOptions = {}, modified: DateObjectUnits = {}) {
        const theDate = DateTime.fromMillis(localMilis);
        return DateTime.fromObject({ ...theDate.toObject(), ...modified }, opts);
    }
}
